import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Loader2, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { subPaymentsApi, projectsApi, subcontractorsApi } from '../api';
import { fmtDate, centsToDollarString } from '../constants';
import { SUB_PAY_METHOD_OPTIONS, subPayMethodLabel } from '../subConstants';
import { SubPaymentFormDialog } from './SubPaymentFormDialog';

const selectCls = 'bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';

export const SubPaymentsList = ({ lockedProjectId, lockedSubcontractorId, showFilters = false, refreshKey = 0, onChanged }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [subs, setSubs] = useState([]);
  const [filters, setFilters] = useState({ project_id: '', subcontractor_id: '', payment_method: '', status: '', start_date: '', end_date: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!showFilters) return;
    if (!lockedProjectId) projectsApi.list({ sort: 'name_az' }).then((r) => setProjects(r.data)).catch(() => {});
    if (!lockedSubcontractorId) subcontractorsApi.list({}).then((r) => setSubs(r.data)).catch(() => {});
  }, [showFilters, lockedProjectId, lockedSubcontractorId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (lockedProjectId) p.project_id = lockedProjectId;
      else if (filters.project_id) p.project_id = filters.project_id;
      if (lockedSubcontractorId) p.subcontractor_id = lockedSubcontractorId;
      else if (filters.subcontractor_id) p.subcontractor_id = filters.subcontractor_id;
      if (filters.payment_method) p.payment_method = filters.payment_method;
      if (filters.status) p.status = filters.status;
      if (filters.start_date) p.start_date = filters.start_date;
      if (filters.end_date) p.end_date = filters.end_date;
      const res = await subPaymentsApi.list(p);
      setPayments(res.data);
    } catch {
      toast.error('Could not load payments');
    } finally { setLoading(false); }
  }, [filters, lockedProjectId, lockedSubcontractorId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setF = (k, v) => setFilters((x) => ({ ...x, [k]: v }));

  return (
    <div data-testid="sub-payments-list">
      {showFilters && (
        <div className="flex flex-wrap items-end gap-3 mb-4">
          {!lockedProjectId && (
            <select value={filters.project_id} onChange={(e) => setF('project_id', e.target.value)} className={selectCls} data-testid="sp-filter-project">
              <option value="">All projects</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          {!lockedSubcontractorId && (
            <select value={filters.subcontractor_id} onChange={(e) => setF('subcontractor_id', e.target.value)} className={selectCls} data-testid="sp-filter-sub">
              <option value="">All subcontractors</option>
              {subs.map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)}
            </select>
          )}
          <select value={filters.payment_method} onChange={(e) => setF('payment_method', e.target.value)} className={selectCls} data-testid="sp-filter-method">
            <option value="">All methods</option>
            {SUB_PAY_METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setF('status', e.target.value)} className={selectCls} data-testid="sp-filter-status">
            <option value="">All statuses</option>
            <option value="recorded">Recorded</option>
            <option value="voided">Voided</option>
          </select>
          <Input type="date" value={filters.start_date} onChange={(e) => setF('start_date', e.target.value)} className={`${inputCls} w-auto`} data-testid="sp-filter-start" />
          <Input type="date" value={filters.end_date} onChange={(e) => setF('end_date', e.target.value)} className={`${inputCls} w-auto`} data-testid="sp-filter-end" />
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">
          <Loader2 size={22} className="mx-auto mb-2 animate-spin text-[#D4AF37]" /> Loading payments…
        </div>
      ) : payments.length === 0 ? (
        <Card className="bg-[#111111] border-gray-800 p-10 text-center" data-testid="sp-empty">
          <Banknote size={30} className="mx-auto mb-3 text-gray-700" />
          <p className="text-gray-500">No subcontractor payments recorded yet.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {payments.map((p) => (
            <button
              key={p.id}
              onClick={() => { setActive(p); setDialogOpen(true); }}
              className={`w-full flex flex-wrap items-center gap-x-4 gap-y-1 bg-[#111111] hover:bg-[#1A1A1A] border border-gray-800 rounded-lg px-4 py-3 text-left transition-colors ${p.status === 'voided' ? 'opacity-60' : ''}`}
              data-testid={`sp-row-${p.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium">{p.subcontractor?.display_name}</span>
                  {p.status === 'voided' && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-red-900/40 text-red-400 border-red-800">Voided</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">
                  {fmtDate(p.payment_date)} · {p.project?.name || 'Unknown Project'}
                  {p.commitment ? ` · ${p.commitment.title}` : ' · Unallocated'}
                  {p.payment_method ? ` · ${subPayMethodLabel(p.payment_method)}` : ''}
                  {p.reference_number ? ` · Ref ${p.reference_number}` : ''}
                </div>
              </div>
              <div className="text-sm font-semibold text-gray-100 shrink-0">{centsToDollarString(p.amount_cents)}</div>
            </button>
          ))}
        </div>
      )}

      <SubPaymentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        payment={active}
        onSaved={() => { load(); onChanged?.(); }}
      />
    </div>
  );
};
